import { Routes, Route } from 'react-router-dom';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import {
  AccountDetails,
  AccountSummary,
  AllDownlineListing,
  BannerManagement,
  BetList,
  BetSetting,
  BussinessReports,
  CasinoBetList,
  CasinoControl,
  CasinoDetail,
  CasinoPlByDownline,
  CasinoPlByPlayer,
  CategoryControl,
  ContentManagement,
  DashboardPage,
  EventManagement,
  EventFixtures,
  FilteredGames,
  GameControl,
  InactiveMemerList,
  Login,
  NotFoundPage,
  NotificationManagement,
  OnboardingPage,
  PlByEvent,
  PlDownline,
  ProviderControl,
  Setting,
  SubUsers,
  SupportContactManagement,
  TransactionCode,
  TransactionHistory,
  UserAccountStatement,
  SettlementReport,
  // UserBetHistory,
  UserBettingPL,
  MemberSportSettings,
  MemberChipSummary,
  MemberUserSettings,
  RoleWiseAccountStatement,
  StatementBets,
  DwTransection,
  Settlement,
  AddPromotion,
  CreateClient,
  SportAnalysis,
  SportAnalysisEventDetails,
  SportAnalysisBetHistory,
  MultiMarket,
  UserSportsBets,
  UserCasinoBets,
  SportsPLEvent,
  SportsPLSummary,
  SportsPLMarketBets,
  SportsSummaryReport,
  ChipSettlement,
  // BalanceSheet,
  SettlementHistory,
  TopClients,
  // GamesDashboard,
} from '@/components/routes';
import { useRoleCheck } from './hooks/useRoleCheck';
import ProtectedAdminRoutes from './components/routes/ProtectedAdminRoutes';

export function App() {
  const { isOwner, isRoleUser } = useRoleCheck();
  const ownerOnly = (element: React.ReactNode) => (
    <ProtectedAdminRoutes allowed={isOwner || isRoleUser}>{element}</ProtectedAdminRoutes>
  );
  return (
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        {/* <Route path="/register" element={<RegisterPage />} /> */}
      </Route>

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* <Route path="/profile" element={<ProfilePage />} /> */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          {/* Downline Routes */}
          <Route path="/members/:id" element={<AllDownlineListing />} />
          <Route path="/members/:id/:role/:user/:memberid/:upline" element={<SubUsers />} />
          <Route path="/create-client/:id" element={<CreateClient />} />
          <Route path="/settlement" element={<Settlement />} />
          <Route path="/members/:id/sub/:memberid/:path/:userType" element={<AccountDetails />}>
            <Route index element={<AccountSummary />} />
            <Route path="account-summary" element={<AccountSummary />} />

            <Route path="account-statement/:statementType?" element={<UserAccountStatement />} />
            <Route
              path="account-statement/:statementType/bets/:eventId/:marketId"
              element={<StatementBets />}
            />
            <Route path="settlement-report" element={<SettlementReport />} />
            <Route path="bet-history/:betType" element={<UserSportsBets />} />
            <Route path="bet-history/:betType/:marketId" element={<UserSportsBets />} />
            <Route path="betting-pl" element={<UserBettingPL />} />
            <Route path="betting-pl/casino-event/:gameId" element={<UserCasinoBets />} />
            <Route path="betting-pl/event/:eventId" element={<SportsPLEvent />} />
            <Route
              path="betting-pl/event/:eventId/bets/:marketId"
              element={<SportsPLMarketBets />}
            />
            <Route path="betting-pl/summary" element={<SportsPLSummary />} />
            <Route path="sports-summary" element={<SportsSummaryReport />} />
            <Route path="sport-settings" element={<MemberSportSettings />} />
            <Route path="chip-summary" element={<MemberChipSummary />} />
            <Route path="user-settings" element={<MemberUserSettings />} />

            <Route path="casino-detail" element={<CasinoDetail />} />

            <Route path="transaction-history" element={<TransactionHistory />} />
          </Route>

          {/* Change Password Routes */}
          <Route path="/settings" element={<Setting />} />

          {/* Event Management*/}
          <Route path="/event-management/:status?" element={ownerOnly(<EventManagement />)} />
          <Route path="/event-fixtures" element={ownerOnly(<EventFixtures />)} />
          <Route path="/bet-setting" element={ownerOnly(<BetSetting />)} />
          {/* Casino management */}
          <Route path="/casino-management" element={ownerOnly(<CasinoControl />)}>
            <Route index element={<CategoryControl />} />
            <Route path="provider" element={<ProviderControl />} />
            <Route path="game" element={<GameControl />} />
          </Route>
          <Route path="/casino-management/games" element={ownerOnly(<FilteredGames />)} />
          {/* Content Management */}
          <Route path="/content-management" element={ownerOnly(<ContentManagement />)}>
            <Route index element={<BannerManagement />} />
            <Route path="notification-management" element={<NotificationManagement />} />
            <Route path="support-management" element={<SupportContactManagement />} />
          </Route>
          <Route path="/add-promotion" element={<AddPromotion />} />
          <Route path="/rolewise-account-statement" element={<RoleWiseAccountStatement />} />
          <Route
            path="/rolewise-account-statement/bets/:eventId/:marketId"
            element={<StatementBets backPath="/rolewise-account-statement" />}
          />

          {/* Sport Analysis Routes */}
          <Route path="/sport-analysis" element={<SportAnalysis />} />
          <Route path="/sport-analysis/event/:eventId" element={<SportAnalysisEventDetails />} />
          <Route
            path="/sport-analysis/event/:eventId/bets/:marketId"
            element={<SportAnalysisBetHistory />}
          />
          <Route path="/multi-market" element={<MultiMarket />} />

          {/* Reports Routes */}
          <Route path="/bet-list" element={<BetList />} />
          <Route path="/casino-bet-list" element={<CasinoBetList />} />
          <Route path="/pl-by-downline" element={<PlDownline />} />
          <Route path="/pl-by-player" element={<SettlementReport />} />
          <Route path="/pl-by-event" element={<PlByEvent />} />
          <Route path="/pl-by-event/casino-event/:gameId" element={<CasinoBetList />} />
          <Route path="/pl-by-event/event/:eventId" element={<SportsPLEvent />} />
          <Route
            path="/pl-by-event/event/:eventId/bets/:marketId"
            element={<SportsPLMarketBets />}
          />
          <Route path="/pl-by-event/summary" element={<SportsPLSummary />} />
          <Route path="/casinopl-by-player" element={<CasinoPlByPlayer />} />
          <Route path="/casinopl-by-downline" element={<CasinoPlByDownline />} />
          <Route path="/business-reports" element={<BussinessReports />} />
          <Route path="/reports/chip-summary" element={<MemberChipSummary />} />
          <Route path="/reports/chip-statement" element={<DwTransection />} />
          <Route path="/reports/sports-summary" element={<SportsSummaryReport />} />
          <Route path="/reports/settlement" element={<ChipSettlement />} />
          {/* <Route path="/reports/balance-sheet" element={<BalanceSheet />} /> */}
          <Route path="/reports/settlement-history/:userId" element={<SettlementHistory />} />
          <Route path="/reports/top-clients" element={<TopClients />} />
          <Route path="/inactive-members" element={<InactiveMemerList />} />
        </Route>
      </Route>
      {/* </Route> */}
      <Route path="/transaction-code" element={<TransactionCode />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
